import { ApiError } from "@/api/errors";
import type { FieldMachineOption } from "@/api/fieldLeads";
import type { LegacyNotice, OutboxFailure, OutboxItem } from "@/offline";
import type { FieldLeadContactSource, FieldLeadRecord } from "./historyDb";

type FieldLeadState = "sent" | "waiting" | "retrying" | "failed" | "unconfirmed";

interface FieldLeadRow {
  record: FieldLeadRecord;
  state: FieldLeadState;
  outboxId: string | null;
  failure: OutboxFailure | null;
}

export const FIELD_LEAD_STATE_TEXT: Record<FieldLeadState, string> = {
  sent: "Cargado en el CRM",
  waiting: "En la fila: sale solo apenas haya señal",
  retrying: "Reintentando: la app lo vuelve a probar sola",
  failed: "No se pudo enviar",
  unconfirmed: "Salió de la cola y no quedó confirmado en este teléfono",
};

export const WAITING_SESSION_TEXT = "En la fila: sale solo cuando retomes la sesión";

export const UNCONFIRMED_HINT =
  "Puede estar cargado en el CRM. Antes de cargarlo de nuevo, confirmá en el CRM o con la oficina si llegó: si no, el dato queda duplicado.";

export const STALE_TEXT =
  "Esto lo dejó guardado la versión anterior de la app y desde acá ya no sale. Cargalo en el CRM web.";

export const LEGACY_NOTICE_TEXT: Record<LegacyNotice, string> = {
  "approval-pending":
    "Entró al CRM, pero el precio quedó esperando la autorización de tu jefe. La cotización todavía no se puede mandar.",
  "company-degraded":
    "Entró al CRM, pero sin la empresa: no se pudo verificar el CUIT. Completala en el CRM web.",
};

export const LEGACY_DELIVERED_TEXT = "Entró al CRM con la versión anterior de la app.";

export function legacyNoticeTexts(notices: LegacyNotice[]): string[] {
  return notices.length === 0
    ? [LEGACY_DELIVERED_TEXT]
    : notices.map((notice) => LEGACY_NOTICE_TEXT[notice]);
}

const STALE_KIND_TEXT: Record<string, string> = {
  "deal.intake": "Una operación nueva con su cotización",
  "contact.create": "Un contacto nuevo",
  "activity.complete": "Una visita completada",
  "activity.create": "Un próximo contacto agendado",
  "activity.reschedule": "Un contacto reagendado",
  "activity.set-notes": "Una nota de una actividad",
  "deal.set-notes": "Una nota de una operación",
};

export function staleKindText(kind: string): string {
  return STALE_KIND_TEXT[kind] ?? "Algo cargado con la versión anterior";
}

function queuedItemOf(clientLoadId: string, items: OutboxItem[]): OutboxItem | null {
  return (
    items.find(
      (item) =>
        item.action.kind === "field-lead.create" &&
        item.action.body.client_load_id === clientLoadId,
    ) ?? null
  );
}

export function fieldLeadRows(records: FieldLeadRecord[], items: OutboxItem[]): FieldLeadRow[] {
  return records.map((record) => {
    if (record.delivery === "sent") {
      return { record, state: "sent", outboxId: null, failure: null };
    }
    const item = queuedItemOf(record.clientLoadId, items);
    if (item === null) {
      return { record, state: "unconfirmed", outboxId: null, failure: null };
    }
    if (item.status === "failed") {
      return { record, state: "failed", outboxId: item.id, failure: item.failure };
    }
    return {
      record,
      state: item.attempts === 0 ? "waiting" : "retrying",
      outboxId: item.id,
      failure: null,
    };
  });
}

export function machineDescription(option: FieldMachineOption): string {
  const parts = [option.condition === "nueva" ? "Nueva" : "Usada"];
  parts.push(option.year === null ? "Sin año" : String(option.year));
  return parts.join(" · ");
}

export const MACHINE_GONE_TEXT = "Esa máquina ya no está en el stock. Elegí otra.";
export const MACHINE_TAKEN_TEXT = "Esa máquina ya se vendió o está reservada. Elegí otra.";
export const ACCESSORY_TEXT =
  "Eso es un accesorio, no una máquina. Elegí la máquina que le mostraste al cliente.";
export const NO_BRANCH_TEXT =
  "Tu usuario no tiene sucursal asignada y sin ella no se puede cargar. Pedí en la oficina que te la asignen.";
export const BAD_PHONE_TEXT = "Ese teléfono no parece un número real. Revisalo y volvé a intentar.";
export const BAD_PICKED_PHONE_TEXT =
  "El teléfono de ese contacto no parece un número real. Corregilo en tu agenda o elegí otro contacto.";
const BAD_QUEUED_PHONE_TEXT =
  "Ese teléfono no parece un número real. Descartalo y volvé a cargar el dato con el teléfono correcto.";
export const BAD_QUEUED_PICKED_PHONE_TEXT =
  "Ese teléfono no parece un número real. Descartalo, corregí el teléfono en tu agenda y volvé a cargar el dato.";

function phoneRejectedText(source: FieldLeadContactSource | undefined, queued: boolean): string {
  if (queued) {
    return source === "agenda" ? BAD_QUEUED_PICKED_PHONE_TEXT : BAD_QUEUED_PHONE_TEXT;
  }
  return source === "agenda" ? BAD_PICKED_PHONE_TEXT : BAD_PHONE_TEXT;
}
export const BAD_FORM_TEXT = "Faltan datos o hay algo mal cargado. Revisá el formulario.";
export const FRONT_BUG_TEXT =
  "La app pidió algo que tu usuario no puede hacer. Es un error de la app, no tuyo: hay que reportarlo para que lo arreglen.";
export const UNKNOWN_TEXT = "No se pudo cargar el dato. Probá de nuevo.";

export interface FieldLeadProblem {
  message: string;
  reopenMachines: boolean;
  phoneAtFault: boolean;
  retriable: boolean;
}

function problem(
  message: string,
  extras: Partial<Omit<FieldLeadProblem, "message">> = {},
): FieldLeadProblem {
  return {
    message,
    reopenMachines: extras.reopenMachines ?? false,
    phoneAtFault: extras.phoneAtFault ?? false,
    retriable: extras.retriable ?? false,
  };
}

export const NO_ANSWER_TEXT =
  "No hubo respuesta del servidor. Guardalo para mandarlo cuando haya señal o probá de nuevo.";

export const QUEUE_NO_SIGNAL_TEXT =
  "Todavía no hubo señal para mandarlo. No lo rechazaron: sigue guardado en el teléfono.";

const PHONE_DETAIL = /tel[eé]fono|phone/i;

function rejectionProblem(
  status: number | null | undefined,
  code: string | null | undefined,
  detail: string,
  phoneText: string,
): FieldLeadProblem {
  if (status === undefined || status === null) {
    return problem(NO_ANSWER_TEXT, { retriable: true });
  }
  if (status === 404) {
    return problem(MACHINE_GONE_TEXT, { reopenMachines: true });
  }
  if (status === 409) {
    return code === "AccessoryAsMachineError"
      ? problem(ACCESSORY_TEXT, { reopenMachines: true })
      : problem(MACHINE_TAKEN_TEXT, { reopenMachines: true });
  }
  if (status === 422) {
    if (code !== "ValidationError") {
      return problem(BAD_FORM_TEXT);
    }
    if (detail.includes("branch")) {
      return problem(NO_BRANCH_TEXT);
    }
    return PHONE_DETAIL.test(detail)
      ? problem(phoneText, { phoneAtFault: true })
      : problem(BAD_FORM_TEXT);
  }
  if (status === 403) {
    return problem(FRONT_BUG_TEXT);
  }
  return problem(UNKNOWN_TEXT, { retriable: true });
}

export function fieldLeadProblem(
  error: unknown,
  source: FieldLeadContactSource = "manual",
): FieldLeadProblem {
  if (!(error instanceof ApiError)) {
    return problem(UNKNOWN_TEXT, { retriable: true });
  }
  return rejectionProblem(
    error.status,
    error.code,
    error.message,
    phoneRejectedText(source, false),
  );
}

export function queuedFieldLeadProblem(
  failure: OutboxFailure | null,
  source?: FieldLeadContactSource,
): FieldLeadProblem {
  if (failure === null) {
    return problem(UNKNOWN_TEXT, { retriable: true });
  }
  if (failure.status === null) {
    return problem(QUEUE_NO_SIGNAL_TEXT, { retriable: failure.retriable });
  }
  const translated = rejectionProblem(
    failure.status,
    failure.code,
    failure.message,
    phoneRejectedText(source, true),
  );
  return { ...translated, retriable: translated.retriable && failure.retriable };
}
